/**
 * Aggregate Root - WithdrawalWorkflow
 *
 * Machine à états pour le workflow de retrait sécurisé
 *
 * RÈGLES MÉTIER CRITIQUES:
 * - Le workflow suit un chemin strict avec des guards (conditions)
 * - Aucun retrait sans authentification biométrique
 * - Aucun retrait sans vérification d'identité
 * - Les transitions invalides sont rejetées
 * - Le workflow est auditable (tous les états sont enregistrés)
 */

import { Account } from '../entities/Account';
import { Identity } from '../entities/Identity';
import { Money } from '../value-objects/Money';
import { InvalidStateTransitionError } from '../errors/DomainErrors';

/**
 * États possibles du workflow de retrait
 */
export enum WithdrawalState {
  IDLE = 'IDLE',
  CARD_INSERTED = 'CARD_INSERTED',
  BIOMETRIC_PENDING = 'BIOMETRIC_PENDING',
  BIOMETRIC_SUCCESS = 'BIOMETRIC_SUCCESS',
  BIOMETRIC_FAILED = 'BIOMETRIC_FAILED',
  IDENTITY_VERIFICATION = 'IDENTITY_VERIFICATION',
  IDENTITY_VERIFIED = 'IDENTITY_VERIFIED',
  WITHDRAWAL_FORM = 'WITHDRAWAL_FORM',
  VALIDATION_PENDING = 'VALIDATION_PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

/**
 * Labels français pour l'affichage
 */
export const WITHDRAWAL_STATE_LABELS: Record<WithdrawalState, string> = {
  [WithdrawalState.IDLE]: 'En attente',
  [WithdrawalState.CARD_INSERTED]: 'Carte insérée',
  [WithdrawalState.BIOMETRIC_PENDING]: 'Authentification biométrique en cours',
  [WithdrawalState.BIOMETRIC_SUCCESS]: 'Authentification réussie',
  [WithdrawalState.BIOMETRIC_FAILED]: 'Authentification échouée',
  [WithdrawalState.IDENTITY_VERIFICATION]: 'Vérification d\'identité',
  [WithdrawalState.IDENTITY_VERIFIED]: 'Identité vérifiée',
  [WithdrawalState.WITHDRAWAL_FORM]: 'Saisie du retrait',
  [WithdrawalState.VALIDATION_PENDING]: 'Validation en cours',
  [WithdrawalState.COMPLETED]: 'Retrait complété',
  [WithdrawalState.FAILED]: 'Échec',
  [WithdrawalState.CANCELLED]: 'Annulé',
};

/**
 * Résultat d'authentification biométrique
 */
export interface BiometricAuthResult {
  readonly success: boolean;
  readonly clientId?: string;
  readonly clientName?: string;
  readonly authId?: string;
  readonly timestamp: Date;
  readonly errorCode?: string;
}

/**
 * Client authentifié avec ses informations
 */
export interface AuthenticatedClient {
  readonly clientId: string;
  readonly clientName: string;
  readonly biometricAuthId: string;
  readonly identity: Identity;
  readonly authenticatedAt: Date;
}

/**
 * Contexte du workflow (données accumulées)
 */
export interface WithdrawalWorkflowContext {
  account?: Account;
  authenticatedClients: AuthenticatedClient[];
  lastBiometricResult?: BiometricAuthResult;
  requestedAmount?: Money;
  requestedDenomination?: Map<number, number>;
  destination?: string;
  notes?: string;
  errorMessage?: string;
  transactionId?: string;
  // Pour tracking
  startedAt?: Date;
  completedAt?: Date;
}

/**
 * Transitions valides de la machine à états
 */
const VALID_TRANSITIONS: Record<WithdrawalState, WithdrawalState[]> = {
  [WithdrawalState.IDLE]: [
    WithdrawalState.CARD_INSERTED,
    WithdrawalState.BIOMETRIC_PENDING, // Nouveau: biométrie en premier (sans étape carte séparée)
    WithdrawalState.CANCELLED,
  ],
  [WithdrawalState.CARD_INSERTED]: [
    WithdrawalState.BIOMETRIC_PENDING,
    WithdrawalState.CANCELLED,
  ],
  [WithdrawalState.BIOMETRIC_PENDING]: [
    WithdrawalState.BIOMETRIC_SUCCESS,
    WithdrawalState.BIOMETRIC_FAILED,
  ],
  [WithdrawalState.BIOMETRIC_SUCCESS]: [
    WithdrawalState.IDENTITY_VERIFICATION,
  ],
  [WithdrawalState.BIOMETRIC_FAILED]: [
    WithdrawalState.FAILED,
    WithdrawalState.BIOMETRIC_PENDING, // Permet un retry
    WithdrawalState.CANCELLED,
  ],
  [WithdrawalState.IDENTITY_VERIFICATION]: [
    WithdrawalState.IDENTITY_VERIFIED,
    WithdrawalState.CANCELLED,
  ],
  [WithdrawalState.IDENTITY_VERIFIED]: [
    WithdrawalState.WITHDRAWAL_FORM,
    WithdrawalState.BIOMETRIC_PENDING, // Pour ajouter un autre client (compte joint)
  ],
  [WithdrawalState.WITHDRAWAL_FORM]: [
    WithdrawalState.VALIDATION_PENDING,
    WithdrawalState.CANCELLED,
  ],
  [WithdrawalState.VALIDATION_PENDING]: [
    WithdrawalState.COMPLETED,
    WithdrawalState.FAILED,
  ],
  [WithdrawalState.COMPLETED]: [],
  [WithdrawalState.FAILED]: [
    WithdrawalState.IDLE, // Permet de recommencer
  ],
  [WithdrawalState.CANCELLED]: [
    WithdrawalState.IDLE, // Permet de recommencer
  ],
};

/**
 * Aggregate Root - WithdrawalWorkflow
 */
export class WithdrawalWorkflow {
  private _state: WithdrawalState;
  private _context: WithdrawalWorkflowContext;
  private _stateHistory: Array<{ state: WithdrawalState; timestamp: Date }>;

  constructor() {
    this._state = WithdrawalState.IDLE;
    this._context = {
      authenticatedClients: [],
    };
    this._stateHistory = [{ state: WithdrawalState.IDLE, timestamp: new Date() }];
  }

  // Getters
  get state(): WithdrawalState {
    return this._state;
  }

  get stateLabel(): string {
    return WITHDRAWAL_STATE_LABELS[this._state];
  }

  get context(): Readonly<WithdrawalWorkflowContext> {
    return Object.freeze({ ...this._context });
  }

  get stateHistory(): ReadonlyArray<{ state: WithdrawalState; timestamp: Date }> {
    return [...this._stateHistory];
  }

  /**
   * Vérifie si une transition est valide
   */
  private canTransitionTo(newState: WithdrawalState): boolean {
    return VALID_TRANSITIONS[this._state].includes(newState);
  }

  /**
   * Effectue une transition d'état
   */
  private transitionTo(newState: WithdrawalState): void {
    if (!this.canTransitionTo(newState)) {
      throw new InvalidStateTransitionError(this._state, newState);
    }
    this._state = newState;
    this._stateHistory.push({ state: newState, timestamp: new Date() });
  }

  // === GUARDS (Conditions de passage) ===

  private guardCardInserted(): void {
    if (!this._context.account) {
      throw new Error('Aucun compte associé');
    }
    if (!this._context.account.isActive()) {
      throw new Error('Le compte n\'est pas actif');
    }
  }

  private guardBiometricSuccess(): void {
    // Vérifie seulement qu'on est dans le bon état (BIOMETRIC_SUCCESS)
    // Les clients authentifiés seront ajoutés lors de completeIdentityVerification
    if (this._state !== WithdrawalState.BIOMETRIC_SUCCESS) {
      throw new Error('L\'authentification biométrique n\'a pas été complétée avec succès');
    }
  }

  private guardIdentityVerified(): void {
    if (this._context.authenticatedClients.length === 0) {
      throw new Error('Aucun client authentifié');
    }
    const allHaveIdentity = this._context.authenticatedClients.every(c => c.identity !== undefined);
    if (!allHaveIdentity) {
      throw new Error('Tous les clients doivent avoir leur identité vérifiée');
    }
  }

  private guardWithdrawalFormComplete(): void {
    if (!this._context.requestedAmount || this._context.requestedAmount.isZero()) {
      throw new Error('Le montant doit être supérieur à zéro');
    }
    if (!this._context.requestedDenomination || this._context.requestedDenomination.size === 0) {
      throw new Error('Le billetage doit être renseigné');
    }
    if (!this._context.destination) {
      throw new Error('La destination des fonds doit être renseignée');
    }
  }

  // === ACTIONS / TRANSITIONS ===

  /**
   * Insertion de la carte et lecture du compte
   */
  insertCard(account: Account): void {
    this.transitionTo(WithdrawalState.CARD_INSERTED);
    this._context.account = account;
    this._context.startedAt = new Date();
    this.guardCardInserted();
  }

  /**
   * Associe un compte au workflow (peut être appelé depuis n'importe quel état)
   * Utilisé par le nouveau flux où la carte est détectée pendant l'étape biométrique
   */
  setAccount(account: Account): void {
    this._context.account = account;
    if (!this._context.startedAt) {
      this._context.startedAt = new Date();
    }
    this.guardCardInserted();
  }

  /**
   * Étape 1 (nouveau workflow): Démarrer l'authentification biométrique
   * Peut être appelé directement depuis IDLE (sans passer par CARD_INSERTED)
   */
  startBiometricAuth(): void {
    this.transitionTo(WithdrawalState.BIOMETRIC_PENDING);
  }

  /**
   * Étape 2b: Résultat de l'authentification biométrique
   */
  completeBiometricAuth(result: BiometricAuthResult): void {
    if (result.success) {
      if (!result.clientId || !result.clientName || !result.authId) {
        throw new Error('Résultat d\'authentification incomplet');
      }
      // Stocker le résultat biométrique dans le contexte pour les étapes suivantes
      this._context.lastBiometricResult = result;
      this.transitionTo(WithdrawalState.BIOMETRIC_SUCCESS);
    } else {
      this._context.errorMessage = result.errorCode || 'Échec de l\'authentification';
      this.transitionTo(WithdrawalState.BIOMETRIC_FAILED);
    }
  }

  /**
   * Retry après échec biométrique
   */
  retryBiometricAuth(): void {
    if (this._state !== WithdrawalState.BIOMETRIC_FAILED) {
      throw new Error('Retry non disponible dans cet état');
    }
    this._context.errorMessage = undefined;
    this.transitionTo(WithdrawalState.BIOMETRIC_PENDING);
  }

  /**
   * Étape 3: Démarrer la vérification d'identité
   */
  startIdentityVerification(): void {
    this.guardBiometricSuccess();
    this.transitionTo(WithdrawalState.IDENTITY_VERIFICATION);
  }

  /**
   * Étape 3b: Compléter la vérification d'identité
   */
  completeIdentityVerification(client: AuthenticatedClient): void {
    // Vérifier que la pièce d'identité n'est pas expirée
    if (client.identity.isExpired()) {
      throw new Error('La pièce d\'identité est expirée');
    }

    // Vérifier que le client n'est pas déjà authentifié
    const alreadyAuth = this._context.authenticatedClients.some(
      c => c.clientId === client.clientId
    );
    if (alreadyAuth) {
      throw new Error('Ce client est déjà authentifié');
    }

    // Vérifier autorisation via le compte local si disponible
    // Sinon, on fait confiance aux données CBS (vérification faite côté backend)
    if (this._context.account && !this._context.account.isHolderAuthorized(client.clientId)) {
      throw new Error('Le client n\'est pas autorisé sur ce compte');
    }

    // Ajouter le client authentifié
    this._context.authenticatedClients.push(client);

    this.transitionTo(WithdrawalState.IDENTITY_VERIFIED);
  }

  /**
   * Passer au formulaire de retrait
   */
  proceedToWithdrawalForm(): void {
    this.guardIdentityVerified();
    this.transitionTo(WithdrawalState.WITHDRAWAL_FORM);
  }

  /**
   * Ajouter un autre client (pour compte joint)
   */
  addAnotherClient(): void {
    if (this._state !== WithdrawalState.IDENTITY_VERIFIED) {
      throw new Error('Impossible d\'ajouter un client dans cet état');
    }
    if (!this._context.account?.isJoint()) {
      throw new Error('Seuls les comptes joints peuvent avoir plusieurs clients');
    }
    this.transitionTo(WithdrawalState.BIOMETRIC_PENDING);
  }

  /**
   * Étape 4: Définir les détails du retrait
   */
  setWithdrawalDetails(
    amount: Money,
    denomination: Map<number, number>,
    destination: string,
    notes?: string
  ): void {
    if (this._state !== WithdrawalState.WITHDRAWAL_FORM) {
      throw new Error('Les détails ne peuvent être saisis que dans l\'état WITHDRAWAL_FORM');
    }

    if (!amount.isPositive()) {
      throw new Error('Le montant doit être positif');
    }

    this._context.requestedAmount = amount;
    this._context.requestedDenomination = denomination;
    this._context.destination = destination;
    this._context.notes = notes;
  }

  /**
   * Étape 5: Soumettre pour validation
   */
  submitForValidation(): void {
    this.guardWithdrawalFormComplete();
    this.transitionTo(WithdrawalState.VALIDATION_PENDING);
  }

  /**
   * Étape 6: Compléter le retrait
   */
  complete(transactionId: string): void {
    this.transitionTo(WithdrawalState.COMPLETED);
    this._context.transactionId = transactionId;
    this._context.completedAt = new Date();
  }

  /**
   * Échec du workflow
   */
  fail(errorMessage: string): void {
    this._context.errorMessage = errorMessage;
    this.transitionTo(WithdrawalState.FAILED);
  }

  /**
   * Annuler le workflow
   */
  cancel(): void {
    this.transitionTo(WithdrawalState.CANCELLED);
  }

  /**
   * Réinitialiser le workflow
   */
  reset(): void {
    this._state = WithdrawalState.IDLE;
    this._context = {
      authenticatedClients: [],
    };
    this._stateHistory = [{ state: WithdrawalState.IDLE, timestamp: new Date() }];
  }

  /**
   * Vérifications d'état
   */
  isIdle(): boolean {
    return this._state === WithdrawalState.IDLE;
  }

  isInProgress(): boolean {
    return ![
      WithdrawalState.IDLE,
      WithdrawalState.COMPLETED,
      WithdrawalState.FAILED,
      WithdrawalState.CANCELLED,
    ].includes(this._state);
  }

  isCompleted(): boolean {
    return this._state === WithdrawalState.COMPLETED;
  }

  isFailed(): boolean {
    return this._state === WithdrawalState.FAILED;
  }

  isCancelled(): boolean {
    return this._state === WithdrawalState.CANCELLED;
  }

  canCancel(): boolean {
    return this.canTransitionTo(WithdrawalState.CANCELLED);
  }

  canRetry(): boolean {
    return this._state === WithdrawalState.BIOMETRIC_FAILED;
  }

  /**
   * Sérialise pour persistence / logging
   */
  toJSON(): Record<string, unknown> {
    return {
      state: this._state,
      context: {
        ...this._context,
        account: this._context.account?.toJSON(),
        requestedAmount: this._context.requestedAmount?.toJSON(),
        requestedDenomination: this._context.requestedDenomination
          ? Array.from(this._context.requestedDenomination.entries())
          : undefined,
        authenticatedClients: this._context.authenticatedClients.map(c => ({
          clientId: c.clientId,
          clientName: c.clientName,
          biometricAuthId: c.biometricAuthId,
          identity: c.identity.toJSON(),
          authenticatedAt: c.authenticatedAt.toISOString(),
        })),
      },
      stateHistory: this._stateHistory,
    };
  }
}
